/* eslint-disable complexity */
/* eslint-disable max-lines */
import { A11yModule } from '@angular/cdk/a11y';
import { LayoutModule } from '@angular/cdk/layout';
import { OverlayModule } from '@angular/cdk/overlay';
import { DatePipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  GenericAction,
  GlobalTabsComponent,
  SelectedTab,
  Tab
} from '@com-delta-omnichannel/mach';
import {
  IdpPredictiveSearchComponent,
  PredictiveSearch
} from './components/predictive-search';
import { RecentListItem } from './models/search-modal-input.interface';

@Component({
  standalone: true,
  selector: 'idp-book-widget-search-modal',
  styleUrls: ['book-widget-search-modal.component.scss'],
  templateUrl: 'book-widget-search-modal.component.html',
  imports: [
    OverlayModule,
    GlobalTabsComponent,
    IdpPredictiveSearchComponent,
    NgFor,
    NgTemplateOutlet,
    NgIf,
    LayoutModule,
    DatePipe,
    A11yModule
  ],
  encapsulation: ViewEncapsulation.None
})
export class BookWidgetSearchModalComponent {
  private itemSelected = false;
  protected selectedTabIndex = 0;
  private predictiveSearchRef!: IdpPredictiveSearchComponent;
  valueChangeOnInput = false;
  @Input() searchModalTabs: Tab[] = [];
  @Input() savedSearchList: string[] = [];
  @Input() recentSearchList: RecentListItem[] = [];
  @Input() predictiveSearchData: PredictiveSearch = {
    placeholder: '',
    items: [],
    title: '',
    value: '',
    transparentSearch: true,
    errorMessage: '',
    selectLabel: ''
  };

  @Input() isFullscreen = false;
  @Input() modalOpen = false;

  /**
   * emits when tab is changed
   */
  @Output()
  selectedTabChange = new EventEmitter<SelectedTab>();
  /**
   * emits when search modal is closed, either by close button click,
   * or by clicking outside modal
   */

  @Output() searchModalClose = new EventEmitter<string>();
  /**
   * emits the input text when something is being typed in search input
   */
  @Output() searchStringChanged = new EventEmitter<string>();
  /**
   * emits with ListItem when dropdown item is clicked
   */
  @Output() predictiveSearchItemSelected = new EventEmitter<
    GenericAction<string>
  >();

  @Output() recentSearchItemSelected = new EventEmitter<number>();
  @Output() savedSearchItemSelected = new EventEmitter<number>();
  @ViewChild(IdpPredictiveSearchComponent) set ref(
    predictiveSearchRef: IdpPredictiveSearchComponent | undefined
  ) {
    if (predictiveSearchRef) {
      this.predictiveSearchRef = predictiveSearchRef;
    }
  }

  valueChangeOnInputHandler() {
    this.valueChangeOnInput = true;
  }

  protected closeModal(fromCloseIcon = false) {
    this.modalOpen = false;
    this.selectedTabIndex = 0;
    let searchText = '';
    if (
      fromCloseIcon &&
      this.valueChangeOnInput &&
      this.getSelectedTabId() === 'search' &&
      this.predictiveSearchRef?.searchText?.length === 3
    ) {
      searchText = this.predictiveSearchRef.searchText;
    }
    this.valueChangeOnInput = false;
    this.searchModalClose.emit(searchText);
  }

  protected handleTabChange(tab: SelectedTab) {
    this.selectedTabIndex = tab.selectedTabIndex;
    this.selectedTabChange.emit(tab);
  }

  // This uses a flag itemSelected that prevents
  // handleValueChange from emitting when an item is being selected.
  // The flag is reset asynchronously with setTimeout to ensure it doesn't interfere with subsequent user typing.
  protected handleChoose(chosen: GenericAction<string>) {
    this.itemSelected = true;
    this.closeModal();
    this.predictiveSearchItemSelected.emit(chosen);
    setTimeout(() => (this.itemSelected = false), 0);
  }

  protected handleValueChange(searchString: string) {
    if (!this.itemSelected && searchString.length >= 3) {
      this.searchStringChanged.emit(searchString);
    }
  }

  protected handleRecentOrSavedSearchItemClick(
    type: 'saved' | 'recent',
    index: number
  ) {
    type === 'recent'
      ? this.recentSearchItemSelected.emit(index)
      : this.savedSearchItemSelected.emit(index);
    this.closeModal();
  }

  protected getSelectedTabId(): string {
    return this.searchModalTabs[this.selectedTabIndex]?.id || '';
  }
}
